import { Link } from 'react-router-dom'
import type { UseQueryResult } from '@tanstack/react-query'
import { errorMessage } from '../lib/api'
import { displayCredits } from '../lib/format'
import { useCredits, useQueue, useScheduledPosts } from '../lib/queries'
import { ErrorBox } from '../components/QueryState'

export function Dashboard() {
  const queue = useQueue()
  const scheduled = useScheduledPosts()
  const credits = useCredits()

  // 「承認待ち」は人の判断を待っているものだけ（生成中・失敗・ストック等は数えない）
  const pendingCount = queue.data?.filter(i => i.post.status === 'pending_approval' || i.post.status === 'draft').length
  const failedQueries = [
    { q: queue as UseQueryResult<unknown>, label: '承認キュー' },
    { q: scheduled as UseQueryResult<unknown>, label: '予約済みの投稿' },
    { q: credits as UseQueryResult<unknown>, label: 'クレジット残高' },
  ].filter(({ q }) => q.isError)

  return (
    <div className="grid gap-5">
      <h1 className="text-2xl font-bold leading-snug">ダッシュボード</h1>
      {failedQueries.map(({ q, label }) => (
        <ErrorBox key={label} message={`${label}の読み込みに失敗しました: ${errorMessage(q.error)}`}
          onRetry={() => { void q.refetch() }} />
      ))}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Stat label="承認待ち" value={display(queue, pendingCount)} unit="件" to="/queue" />
        <Stat label="予約済み" value={display(scheduled, scheduled.data?.length)} unit="件" to="/queue#stock" />
        {/* 残高はトップバーと同じ表示（5単位繰上げ）。読み込み中・取得失敗は「—」 */}
        <Stat label="クレジット残高"
          value={credits.data ? displayCredits(credits.data.balance).toLocaleString() : '—'} unit="cr" />
      </div>
      <section className="gm-card p-[18px]" aria-labelledby="howto-h">
        <h2 id="howto-h" className="text-sm font-bold">はじめかた</h2>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-ink-muted">
          <li><Link className="font-bold text-primary underline" to="/personas">ペルソナ</Link>を作成して X / WordPress を接続する</li>
          <li><Link className="font-bold text-primary underline" to="/compose">投稿を作る</Link>でお題を入れて生成する</li>
          <li><Link className="font-bold text-primary underline" to="/queue">承認キュー</Link>で確認して投稿・予約する</li>
        </ol>
      </section>
    </div>
  )
}

/** 読み込み中は「…」、取得失敗は「—」（失敗の詳細は上の ErrorBox） */
function display(q: UseQueryResult<unknown>, value: string | number | undefined): string | number {
  if (value !== undefined) return value
  return q.isError ? '—' : '…'
}

function Stat({ label, value, unit, to }: { label: string; value: string | number; unit: string; to?: string }) {
  const body = (
    <>
      <div className="text-xs text-ink-muted">{label}</div>
      <div className="gm-num text-[26px] font-bold leading-snug">
        {value}
        <span className="ml-1 text-[13px] font-medium text-ink-muted">{unit}</span>
      </div>
    </>
  )
  return to
    ? (
      <Link to={to} className="gm-card block px-[18px] py-4 transition-colors hover:border-line-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
        {body}
      </Link>
    )
    : <div className="gm-card px-[18px] py-4">{body}</div>
}
